import FormWizard, {
  formVariantsEnum,
} from "../../../Forms/FormWizard/FormWizard";
import {
  AutomationTypeEnum,
  AutomationFormBaseFields,
  validateAutomationData,
} from "../../AutomationsUtils";
import { GET_AUTOMATION } from "../../AutomationsAPI";
import { useMemo, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useQuery } from "@apollo/client";
import FindingsStep from "./FindingsStep";
import NotificationSystemStep, {
  getCustomReportSubject,
} from "../NotificationSystemStep/NotificationSystemStep";
import AutomationSettingsStep from "../AutomationSettingsStep/AutomationSettingsStep";
import useAutomationSubmit from "../useAutomationSubmit/useAutomationSubmit";
import useAutomationURLData, {
  AutomationURLDataReturn,
} from "../useAutomationURLData/useAutomationURLData";
import { PageNotFound } from "@SeemplicityDev/shared";
import useAutomationWizardName from "../useAutomationWizardName/useAutomationWizardName";
import {
  integrationTypeToPageLinkMap,
  ticketEndpointTypeEnum,
} from "@SeemplicityDev/ticket-master";

export const FINDINGS_REPORT_SUBJECT_DEFAULT =
  "Seemplicity Findings Report {{date}}";

interface FindingsReportFormFields extends AutomationFormBaseFields {
  ticketProvider: string;
  integrationType: string;
}

interface FindingsReportOutputParams {
  report_fields: string[];
  ticket_provider_id: string;
  keys: Record<string, any>;
  report_subject?: string;
}

interface FindingsReportInputParams {
  ticket_provider_id: string;
  ticket_provider_type: string;
  field_mapping_id: string;
  report_subject?: string;
}

const validateFindingsReportInputParams = (params: any): boolean => {
  if (!params) {
    return false;
  }

  try {
    const parsedParams = JSON.parse(params);

    return (
      parsedParams.ticket_provider_id &&
      parsedParams.ticket_provider_type &&
      parsedParams.field_mapping_id
    );
  } catch (e) {
    return false;
  }
};

const FindingsReport: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const automationURLData = useAutomationURLData();

  const { onSubmit, createAutomationLoading, editAutomationLoading } =
    useAutomationSubmit({
      automationType: AutomationTypeEnum.FINDINGS_REPORT,
      automationId: id,
    });

  const { data, loading } = useQuery(GET_AUTOMATION, {
    variables: {
      id,
    },
    skip: !id,
  });

  const automationData = useMemo(() => {
    if (
      !!data?.get_automation &&
      validateAutomationData(
        data?.get_automation,
        validateFindingsReportInputParams
      )
    ) {
      const automation = data?.get_automation;
      const params: FindingsReportInputParams = JSON.parse(
        automation?.action?.params
      );

      return { automation, params };
    }
  }, [data]);

  const wizardData:
    | FindingsReportFormFields
    | AutomationURLDataReturn
    | undefined = useMemo(() => {
    if (!!automationData) {
      const { automation, params } = automationData;

      return {
        filter: {
          value: {
            id: automation.selection_rule?.filtersid,
          },
        },
        scope: {
          value: {
            id:
              automation.selection_rule?.scopesid ||
              automation.selection_rule?.scope_group,
          },
        },
        name: automation.name,
        schedule: automation.trigger.value,
        ticketProvider: params.ticket_provider_id,
        integrationType: params.ticket_provider_type,
        report_subject:
          params.report_subject ?? FINDINGS_REPORT_SUBJECT_DEFAULT,
      };
    } else if (!!automationURLData) {
      return automationURLData;
    }
  }, [automationData, automationURLData]);

  const wizardName = useAutomationWizardName(
    "List of Findings",
    automationData?.automation?.name
  );

  const steps = useMemo(() => {
    return [
      {
        name: "Findings",
        id: "findings",
        form: <FindingsStep automationId={id} />,
      },
      {
        name: "Notification System",
        id: "notification_system",
        form: (
          <NotificationSystemStep
            fieldMappingId={automationData?.params?.field_mapping_id}
            reportSubjectDefault={FINDINGS_REPORT_SUBJECT_DEFAULT}
          />
        ),
      },
      {
        name: "Scheduling",
        id: "scheduling",
        form: <AutomationSettingsStep />,
      },
    ];
  }, [automationData, id]);

  const handleSubmit = useCallback(
    (data: Record<string, any>) => {
      const reportSubject = getCustomReportSubject(
        data.report_subject,
        FINDINGS_REPORT_SUBJECT_DEFAULT
      );
      const params: FindingsReportOutputParams = {
        report_fields: data.columns,
        ticket_provider_id: data.ticketProvider,
        keys: data.keys,
        ...(reportSubject && { report_subject: reportSubject }),
      };

      onSubmit(
        {
          filter: data.filter,
          scope: data.scope,
          name: data.name,
          schedule: data.schedule,
          params,
        },
        () => {
          history.push(
            `/remediation/monitoring/${integrationTypeToPageLinkMap[ticketEndpointTypeEnum.REPORT]}`
          );
        }
      );
    },
    [id]
  );

  return id && !automationData && !loading ? (
    <PageNotFound
      subtitle={"Oops! Something went wrong..."}
      description={"For help, contact Customer Services"}
      showReachOutButton={false}
    />
  ) : (
    <FormWizard
      submitLabel={`${id ? "Save" : "Create"}`}
      originName={`${id ? "Edit" : "Add"} Automation`}
      wizardName={wizardName}
      steps={steps}
      onCancel={() => {
        history.goBack();
      }}
      wizardData={wizardData}
      onSubmit={handleSubmit}
      isSubmitting={id ? editAutomationLoading : createAutomationLoading}
      originLink={undefined}
      dangerSubmit={false}
      className={""}
      withSummary={true}
      type={formVariantsEnum.PAGE}
      isValidated={Boolean(id)}
    />
  );
};

export default FindingsReport;
